import * as React from 'react';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { DataShape, FormProps, getFormValues, reduxForm } from 'redux-form';
import history from 'login-utils/history';

import { inputMasks } from '@blend/ui-common';

import { clearOnboardingFormError } from 'login-actions/onboardingActions';
import * as onboardingForm from 'login-utils/onboardingForm';
import * as formUtils from 'login-utils/formUtils';
import * as userUtils from 'login-utils/userUtils';
import FormWithSections, { FormSection } from 'login-components/Common/FormWithSections/FormWithSections';
import InputMap from 'login-components/Common/Inputs/InputMap';
import ContactSVG from 'login-components/Common/svg/ContactSVG';
import FinanceSVG from 'login-components/Common/svg/FinanceSVG';
import HouseSVG from 'login-components/Common/svg/HouseSVG';
import LoanSVG from 'login-components/Common/svg/LoanSVG';

import * as featureFlags from 'login-utils/featureFlags';
import States from 'login-constants/states';
import { getWindow } from 'login-utils/globalWrappers';
import store from '../../store';

const { SHARED_CONSTANTS: sharedConstants, DEPLOYMENT_CONFIG } = getWindow();

const termsInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['termsOfUse', 'privacyPolicy', 'eConsent'],
  identifier: 'terms of use and e-consent',
  name: onboardingForm.FIELDS.acceptTermsAndEconsentGiven,
};

const propertyValueValidator = formUtils.generateValidatePositiveInteger(
  'Property Value must be greater than or equal to 0.',
);
const purchasePriceValidator = formUtils.generateValidatePositiveInteger(
  'Purchase price must be greater than or equal to 0.',
);
const annualIncomeValidator = formUtils.generateValidatePositiveInteger(
  'Annual income must be greater than or equal to 0.',
);
const nameValidator = formUtils.generateValidateNoDisallowedCharacters(
  ['.'],
  'Please enter your name without periods.',
);

export function getFormSections(formValues: DataShape): FormSection[] {
  const isRefinance = formValues[onboardingForm.FIELDS.loanPurpose] === 'REFINANCE';
  const isVeteran = !!formValues.areYouAVeteran;
  const currentUser = userUtils.getCurrentUser();
  const formSections: FormSection[] = _.compact([
    {
      header: {
        iconComponent: HouseSVG,
        text: 'Property Info',
      },
      inputs: [
        {
          ...InputMap.SelectInput,
          identifier: 'type of home',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.propertyType,
          label: 'What type of home is it?',
          choices: sharedConstants.PropertyTypes,
        },
        {
          ...InputMap.SelectInput,
          identifier: 'use of home',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.propertyUsageType,
          label: isRefinance ? 'How are you using this home?' : 'How will you use this home?',
          choices: sharedConstants.PropertyUsageTypes,
        },
        {
          ...InputMap.CountySelector,
          identifier: 'county',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.propertyCounty,
          label: isRefinance
            ? 'What county is this home located?'
            : 'Where are you planning to buy a home? (County/Municipality)',
        },
      ],
    },
    {
      header: {
        iconComponent: LoanSVG,
        text: 'Loan Info',
      },
      inputs: isRefinance
        ? _.compact([
            {
              ...InputMap.MoneyInput,
              identifier: 'property value',
              name: onboardingForm.FIELDS.propertyValue,
              label: `What's the value of your property?`,
              validate: propertyValueValidator,
            },
            {
              ...InputMap.MoneyInput,
              identifier: 'loan amount',
              name: onboardingForm.FIELDS.loanAmount,
              label: `What was the loan amount?`,
              validate: formUtils.loanAmountValidator,
            },
            // Only show this if this is a current user, assuming here that this is an SSO loan
            !!currentUser && {
              ...InputMap.ToggleInput,
              identifier: 'is refinancing current loan',
              name: onboardingForm.FIELDS.isRefinancingCurrentCustomerLoan,
              label: `Was this a loan from ${DEPLOYMENT_CONFIG.organization.friendlyName}?`,
              optional: true,
            },
            {
              ...InputMap.SelectInput,
              identifier: 'refinance purpose',
              name: onboardingForm.FIELDS.refiPurpose,
              label: 'What is the purpose of the refinance?',
              choices: sharedConstants.RefinancePurposeTypes,
            },
          ])
        : [
            {
              ...InputMap.MoneyInput,
              identifier: 'purchase price',
              hidePlaceholder: true,
              name: onboardingForm.FIELDS.purchasePrice,
              label: `What's the purchase price?`,
              validate: purchasePriceValidator,
            },
            {
              ...InputMap.MoneyInput,
              identifier: 'down payment',
              hidePlaceholder: true,
              name: onboardingForm.FIELDS.cashDownPayment,
              label: `What's your down payment?`,
              validate: formUtils.cashDownPaymentValidator,
            },
          ],
    },
    {
      header: {
        iconComponent: FinanceSVG,
        text: 'Financial Info',
      },
      inputs: [
        {
          ...InputMap.SelectInput,
          identifier: 'employment type',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.employmentType,
          label: 'Employment type',
          choices: sharedConstants.EmploymentTypeDefinitions,
        },
        {
          ...InputMap.MoneyInput,
          identifier: 'annual income',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.annualIncome,
          label: 'Your annual income',
          validate: annualIncomeValidator,
        },
        {
          ...InputMap.DateInput,
          identifier: 'start date',
          name: onboardingForm.FIELDS.employmentStartDate,
          label: 'Employment start date',
          validate: formUtils.validateDate,
        },
        {
          ...InputMap.SelectInput,
          identifier: 'estimated credit score',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.estimatedCreditScore,
          label: 'Estimated credit score',
          choices: onboardingForm.CREDIT_SCORE_CHOICES,
        },
      ],
    },
    // if there is a current user, assume this is lead gen capture and ask for contact info
    !currentUser && {
      header: {
        iconComponent: ContactSVG,
        text: 'Contact Info',
      },
      inputs: [
        {
          ...InputMap.SimpleTextInput,
          identifier: 'first name',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.firstName,
          label: 'First name',
          validate: nameValidator,
        },
        {
          ...InputMap.SimpleTextInput,
          identifier: 'last name',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.lastName,
          label: 'Last name',
          validate: nameValidator,
        },
        {
          ...InputMap.EmailInput,
          identifier: 'email',
          hidePlaceholder: true,
          name: onboardingForm.FIELDS.email,
          label: 'Email',
          hideIcon: true,
        },
        {
          ...InputMap.PhoneInput,
          identifier: 'phone number',
          name: onboardingForm.FIELDS.homePhone,
          label: 'Phone number',
          normalize: inputMasks.PHONE.normalize,
          mask: inputMasks.PHONE.maskedInputConfig as any,
        },
      ],
    },
    isRefinance && {
      header: {
        iconComponent: ContactSVG,
        text: 'Personal Info',
      },
      inputs: _.compact([
        {
          ...InputMap.ToggleInput,
          identifier: 'are you a veteran',
          name: onboardingForm.FIELDS.areYouAVeteran,
          label: 'Are currently an active military personnel, a veteran, or a surviving spouse?',
          optional: true,
        },
        isVeteran && {
          ...InputMap.SelectInput,
          identifier: 'veteran status',
          name: onboardingForm.FIELDS.veteranStatus,
          label: 'Veteran Status',
          choices: sharedConstants.VeteranStatusCodes,
        },
      ]),
    },
  ]);
  return formSections;
}

interface ShortFormProps {
  formSections: FormSection[];
  error: string;
}

export class ShortForm extends React.Component<
  ShortFormProps & ReactRouter.RouteComponentProps<{}, {}> & FormProps<DataShape, {}, ReduxState>
> {
  goToMilestones = () => {
    const { location } = this.props;
    const search = location.search || /* istanbul ignore next: (UI-5482) */ '';
    history.push(`${States.MILESTONES}${search}`);
  };

  goToUpfrontRates = () => {
    const { location } = this.props;
    const search = location.search || '';
    history.push(`${States.UPFRONT_RATES}${search}`);
  };

  render() {
    const { error, handleSubmit, formSections } = this.props;
    const onSubmit = handleSubmit!(
      featureFlags.isEnabled('upfrontRatesInLogin20190117') ? this.goToUpfrontRates : this.goToMilestones,
    );

    return (
      <FormWithSections
        formSections={formSections}
        termsInputs={featureFlags.isEnabled('truncatedEConsent20180817') ? [termsInput] : []}
        onSubmit={onSubmit}
        error={error}
      />
    );
  }
}

const FormConnectedShortForm = reduxForm<any, any>({
  ...onboardingForm.FORM_CONFIG,
  validate: (values: DataShape, { formSections }: ShortFormProps) => {
    const allInputs = featureFlags.isEnabled('truncatedEConsent20180817')
      ? _.concat([termsInput], _.flatten(_.map(formSections, definition => definition.inputs)))
      : _.flatten(_.map(formSections, definition => definition.inputs));
    return formUtils.validate(allInputs, values) as any;
  },
  onSubmitSuccess: () => {
    store.dispatch(clearOnboardingFormError());
  },
})(ShortForm);

const ConnectedShortForm = connect((state: ReduxState) => {
  const formValues = getFormValues(onboardingForm.FORM_NAME)(state);
  return {
    formSections: getFormSections(formValues),
    error: _.get(state, 'onboarding.onboardingFormError') as string,
  };
})(FormConnectedShortForm);

export default ConnectedShortForm;
